// src/components/CierreMes.jsx
import { useMemo, useState, useEffect } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { obtenerPagoPorId } from "../api";

export default function CierreMes({
  mes,
  setMes,
  pagos,
  resumen, // ya no lo usamos, pero lo dejamos en la firma por compatibilidad
  filtroEspecialidades,
  setFiltroEspecialidades,
  specialtiesOptions,
  loading,
  error,
}) {
  // 1) Filtro por especialidad (como ya tenías)
  const filtrarPorEspecialidad = (items) =>
    items.filter((p) => {
      const code = (p.specialty_code || "").toLowerCase();
      if (!code) return true;
      if (filtroEspecialidades[code] === undefined) return true;
      return filtroEspecialidades[code];
    });

  const pagosFiltrados = filtrarPorEspecialidad(pagos);

  // =========================================================
  // ✅ CUADRO PARA DESCARGA (independiente del filtro del mes)
  // =========================================================

  const [dlTarjeta, setDlTarjeta] = useState(true);
  const [dlEfectivo, setDlEfectivo] = useState(true);

  const [dlEspecialidades, setDlEspecialidades] = useState({});

  useEffect(() => {
    setDlEspecialidades((prev) => {
      const next = { ...prev };
      (specialtiesOptions || []).forEach((s) => {
        const k = String(s.code).toLowerCase();
        if (next[k] === undefined) next[k] = true;
      });
      return next;
    });
  }, [specialtiesOptions]);

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [downloadInfo, setDownloadInfo] = useState("");

  const pagosParaDescarga = useMemo(() => {
    return (pagos || []).filter((p) => {
      const code = String(p.specialty_code || "").toLowerCase();
      const method = String(p.payment_method || "").toLowerCase();

      if (code && dlEspecialidades[code] === false) return false;

      if (method === "tarjeta" && !dlTarjeta) return false;
      if (method === "efectivo" && !dlEfectivo) return false;

      return true;
    });
  }, [pagos, dlEspecialidades, dlTarjeta, dlEfectivo]);

  // =========================================================
  // ✅ Helpers factura (IVA / Exención + concepto mes)
  // =========================================================

  function esSanitarioExento(factura) {
    const serviceText = String(factura.service_name || "").toLowerCase();
    const specialtyCode = String(factura.specialty_code || "").toLowerCase(); // si viene

    // Preferimos specialty_code si existe
    if (specialtyCode) {
      return (
        specialtyCode.includes("psicologia") ||
        specialtyCode.includes("logop") ||
        specialtyCode.includes("nutric")
      );
    }

    // Fallback por texto del servicio
    return (
      serviceText.includes("psicolog") ||
      serviceText.includes("psicoterapia") ||
      serviceText.includes("logoped") ||
      serviceText.includes("nutric")
    );
  }

  function esYoga(factura) {
    const serviceText = String(factura.service_name || "").toLowerCase();
    const specialtyCode = String(factura.specialty_code || "").toLowerCase();
    return serviceText.includes("yoga") || specialtyCode === "yoga";
  }

  function conceptoConMes(factura) {
    const serviciosMes = new Set([
      "intervencion_psicoterapia_mes",
      "intervencion_estimulacion_mes",
      "intervencion_reeducacion_NEAE_mes",
      "intervencion_logopedia_mes",
      "intervencion_logopedia_NEAE_mes",
    ]);

    const posibleFechaSesion = factura.session_start || factura.date;
    const d = new Date(posibleFechaSesion);
    const fecha = Number.isNaN(d.getTime()) ? new Date(factura.date) : d;

    const rawCode = factura.service_name || "";
    let concepto = rawCode.replace(/_/g, " ");

    if (serviciosMes.has(rawCode)) {
      const mesTexto = fecha.toLocaleDateString("es-ES", {
        month: "long",
        year: "numeric",
      });

      if (/mes/i.test(concepto)) concepto = concepto.replace(/mes/i, `mes ${mesTexto}`);
      else concepto = `${concepto} (${mesTexto})`;
    }

    return { concepto, fecha };
  }

  function generarPdfDesdeFactura(factura) {
    const baseAmount = parseFloat(factura.amount) || 0;

    const isYogaFactura = esYoga(factura);
    const isExentoIVA = !isYogaFactura && esSanitarioExento(factura);

    const iva = isYogaFactura ? baseAmount * 0.21 : 0;
    const total = baseAmount + iva;

    const exencionIVA =
      "Operación exenta de IVA según el artículo 20.Uno.3 de la Ley 37/1992 del IVA.";

    const { concepto, fecha } = conceptoConMes(factura);
    const mesFacturadoTexto = fecha.toLocaleDateString("es-ES", {
      month: "long",
      year: "numeric",
    });

    const pdf = new jsPDF("p", "mm", "a4");

    // Encabezado simple
    pdf.setFontSize(14);
    pdf.text("TE AYUDO - FACTURA", 20, 20);

    pdf.setFontSize(10);
    pdf.text("Avenida Alcalde Jesús Mantaras, local 5", 20, 28);
    pdf.text("Jerez de la Frontera, Cádiz 11405", 20, 33);
    pdf.text("Tel: +34 601 866 199", 20, 38);

    // Datos
    pdf.setFontSize(12);
    pdf.text(`Factura Nº: ${factura.id}`, 20, 55);
    pdf.text(
      `Fecha pago: ${new Date(factura.date).toLocaleDateString("es-ES")}`,
      20,
      63
    );
    pdf.text(`Mes facturado: ${mesFacturadoTexto}`, 20, 71);
    pdf.text(`Paciente: ${factura.paciente || "—"}`, 20, 79);

    // Servicio (partimos líneas si es largo)
    const servicioTxt = `Servicio: ${concepto}`;
    const servicioLines = pdf.splitTextToSize(servicioTxt, 170);
    pdf.text(servicioLines, 20, 87);

    let y = 87 + servicioLines.length * 6 + 8;

    if (factura.profesional_nombre) {
      pdf.setFontSize(11);
      pdf.text(
        `Profesional: ${factura.profesional_nombre} ${factura.profesional_apellidos || ""}`.trim(),
        20,
        y
      );
      y += 8;
      pdf.text(`NIF: ${factura.profesional_nif || "—"}`, 20, y);
      y += 12;
    }

    y = Math.max(y, 130);

    // Detalle importes
    pdf.setFontSize(12);
    pdf.text("Detalle", 20, y);
    y += 8;

    pdf.setFontSize(11);
    pdf.text(`Base: ${baseAmount.toFixed(2)} €`, 20, y);
    y += 8;

    if (isYogaFactura) {
      pdf.text(`IVA (21%): ${iva.toFixed(2)} €`, 20, y);
      y += 8;
    }

    pdf.setFontSize(12);
    pdf.text(`TOTAL: ${total.toFixed(2)} €`, 20, y + 8);

    // ✅ Exención SOLO para psicología/logopedia/nutrición (y NO yoga)
    if (isExentoIVA) {
      pdf.setFontSize(10);
      const lines = pdf.splitTextToSize(exencionIVA, 170);
      pdf.text(lines, 20, y + 22);
    }

    return pdf.output("arraybuffer");
  }

  // =========================================================
  // ✅ Descargar ZIP (frontend)
  // =========================================================
  const onDescargarZip = async () => {
    setDownloadError("");
    setDownloadInfo("");

    if (!dlTarjeta && !dlEfectivo) {
      setDownloadError("Selecciona al menos un método (tarjeta o efectivo).");
      return;
    }

    if (pagosParaDescarga.length === 0) {
      setDownloadError("No hay facturas con los filtros seleccionados.");
      return;
    }

    try {
      setDownloading(true);

      const zip = new JSZip();
      const folder = zip.folder(`${mes}`);

      for (let i = 0; i < pagosParaDescarga.length; i++) {
        const p = pagosParaDescarga[i];
        setDownloadInfo(`Generando ${i + 1}/${pagosParaDescarga.length} (Factura ${p.id})...`);

        const r = await obtenerPagoPorId(p.id);
        const factura = r.item;

        const pdfArrayBuffer = generarPdfDesdeFactura(factura);
        folder.file(`factura_${factura.id}.pdf`, pdfArrayBuffer);
      }

      setDownloadInfo("Creando ZIP...");
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `${mes}_facturas.zip`);

      setDownloadInfo("");
    } catch (e) {
      setDownloadError(e?.message || "Error generando ZIP.");
    } finally {
      setDownloading(false);
    }
  };

  // 2) Resumen dinámico según filtros (total / efectivo / tarjeta / nº pagos)
  const calcularResumen = (items) => {
    let total = 0;
    let efectivo = 0;
    let tarjeta = 0;

    items.forEach((p) => {
      const amt = Number(p.amount) || 0;
      total += amt;
      if (p.payment_method === "efectivo") efectivo += amt;
      else if (p.payment_method === "tarjeta") tarjeta += amt;
    });

    return {
      total,
      efectivo,
      tarjeta,
      count: items.length,
    };
  };

  const resumenFiltrado = calcularResumen(pagosFiltrados);

  // 3) Comisiones por especialidad para el centro
  const calcularComisionCentro = (items, code, porcentajeCentro) => {
    let suma = 0;
    items.forEach((p) => {
      const c = (p.specialty_code || "").toLowerCase();
      if (c === code) {
        suma += Number(p.amount) || 0;
      }
    });
    return suma * porcentajeCentro;
  };

  // Logopedia / nutrición → 30% centro
  const comisionLogopedia = calcularComisionCentro(pagosFiltrados, "logopeda", 0.3);
  const comisionNutricion = calcularComisionCentro(pagosFiltrados, "nutricion", 0.3);

  // Psicología adultos → 40% centro
  const comisionPsicoAdultos = calcularComisionCentro(
    pagosFiltrados,
    "psicologia-adultos",
    0.4
  );

  // 4) Total REAL del centro:
  const calcularTotalRealCentro = (items) => {
    let total = 0;
    let efectivo = 0;
    let tarjeta = 0;

    items.forEach((p) => {
      const amt = Number(p.amount) || 0;
      const code = (p.specialty_code || "").toLowerCase();

      let factor = 1; // por defecto 100% centro
      if (code === "logopeda" || code === "nutricion") factor = 0.3;
      else if (code === "psicologia-adultos") factor = 0.4;

      const real = amt * factor;
      total += real;

      if (p.payment_method === "efectivo") efectivo += real;
      else if (p.payment_method === "tarjeta") tarjeta += real;
    });

    return { total, efectivo, tarjeta };
  };

  const totalRealCentro = calcularTotalRealCentro(pagosFiltrados);

  return (
    <div className="cierre-panel">
      <div className="pagos-actions">
        <div className="month-filter">
          <label>
            Mes:
            <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} />
          </label>
        </div>

        {/* ✅ ESTE ES TU FILTRO ORIGINAL PARA VER EL MES (NO SE TOCA) */}
        <div className="especialidad-filter">
          {specialtiesOptions.map((s) => (
            <label key={s.code} className="chk-especialidad">
              <input
                type="checkbox"
                checked={!!filtroEspecialidades[s.code]}
                onChange={(e) =>
                  setFiltroEspecialidades((prev) => ({
                    ...prev,
                    [s.code]: e.target.checked,
                  }))
                }
              />
              {s.label}
            </label>
          ))}
        </div>
      </div>

      {/* ✅ CUADRO NUEVO PARA DESCARGAR FACTURAS (SEPARADO) */}
      <div
        className="cierre-panel"
        style={{ marginTop: 12, padding: 12, border: "1px solid #ddd", borderRadius: 10 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <strong>Descargar facturas del mes ({mes})</strong>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Estos filtros solo afectan al ZIP, no a la tabla.
            </div>
          </div>

          <button type="button" className="btn" onClick={onDescargarZip} disabled={downloading || loading}>
            {downloading ? "Generando ZIP..." : "Descargar ZIP"}
          </button>
        </div>

        {/* Métodos de pago (solo descarga) */}
        <div style={{ marginTop: 10, display: "flex", gap: 16, flexWrap: "wrap" }}>
          <label className="chk-especialidad">
            <input type="checkbox" checked={dlTarjeta} onChange={(e) => setDlTarjeta(e.target.checked)} />
            💳 Tarjeta
          </label>
          <label className="chk-especialidad">
            <input type="checkbox" checked={dlEfectivo} onChange={(e) => setDlEfectivo(e.target.checked)} />
            💵 Efectivo
          </label>
        </div>

        {/* Especialidades (solo descarga) */}
        <div className="especialidad-filter" style={{ marginTop: 10 }}>
          {specialtiesOptions.map((s) => {
            const code = String(s.code).toLowerCase();
            return (
              <label key={`dl-${s.code}`} className="chk-especialidad">
                <input
                  type="checkbox"
                  checked={dlEspecialidades[code] !== false}
                  onChange={(e) =>
                    setDlEspecialidades((prev) => ({
                      ...prev,
                      [code]: e.target.checked,
                    }))
                  }
                />
                {s.label}
              </label>
            );
          })}
        </div>

        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
          Facturas incluidas: <strong>{pagosParaDescarga.length}</strong>
        </div>

        {downloadInfo ? <div style={{ marginTop: 8, fontSize: 12 }}>{downloadInfo}</div> : null}
        {downloadError ? <div style={{ marginTop: 8, color: "red" }}>{downloadError}</div> : null}
      </div>

      {loading ? (
        <p>Cargando cierre de mes...</p>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : (
        <>
          <div className="cierre-resumen">
            {/* 🔹 Resumen según filtros */}
            <div className="cierre-card">
              <strong>Total mes:</strong> {resumenFiltrado.total.toFixed(2)} €
            </div>
            <div className="cierre-card">
              <strong>Efectivo:</strong> {resumenFiltrado.efectivo.toFixed(2)} €
            </div>
            <div className="cierre-card">
              <strong>Tarjeta:</strong> {resumenFiltrado.tarjeta.toFixed(2)} €
            </div>
            <div className="cierre-card">
              <strong>Nº pagos:</strong> {resumenFiltrado.count}
            </div>

            {/* 🔹 Comisiones para el centro */}
            <div className="cierre-card">
              <strong>Logopedia 30% (centro):</strong> {comisionLogopedia.toFixed(2)} €
            </div>
            <div className="cierre-card">
              <strong>Nutrición 30% (centro):</strong> {comisionNutricion.toFixed(2)} €
            </div>
            <div className="cierre-card">
              <strong>Psicología Adultos 40% (centro):</strong> {comisionPsicoAdultos.toFixed(2)} €
            </div>

            {/* 🔹 Total REAL del centro */}
            <div className="cierre-card cierre-card-destacado">
              <strong>Total real centro:</strong> {totalRealCentro.total.toFixed(2)} €
            </div>
          </div>

          {pagosFiltrados.length === 0 ? (
            <p>No hay pagos en este mes.</p>
          ) : (
            <table className="pv-table pagos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Paciente</th>
                  <th>Especialidad</th>
                  <th>Servicio</th>
                  <th>Importe (€)</th>
                  <th>Método</th>
                </tr>
              </thead>
              <tbody>
                {pagosFiltrados.map((p) => (
                  <tr key={p.id}>
                    <td>{new Date(p.date).toLocaleDateString("es-ES")}</td>
                    <td className="paciente-cell" title={`${p.paciente} ${p.apellidos || ""}`}>
                      <strong>{p.paciente}</strong> {p.apellidos}
                    </td>
                    <td>{p.specialty_name || p.specialty_code || "—"}</td>
                    <td>{p.service_name || "-"}</td>
                    <td style={{ textAlign: "center" }}>{p.amount} €</td>
                    <td>{p.payment_method === "efectivo" ? "💵 Efectivo" : "💳 Tarjeta"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
